// Messages échangés entre client et serveur (JSON pour l'instant ; un format binaire viendra plus tard).
import type { HideoutState } from './hideout';
import type { Grid, ItemInst } from './inventory';
import type { QuestState } from './quests';
import type { MoveInput } from './sim';
import type { ModSlot, WeaponId } from './weapons';
import type { RaidCond } from './atmos';

// Emplacements d'équipement et de stockage
export type Place = 'stash' | 'bag' | 'pouch' | 'primary' | 'secondary' | 'helmet' | 'vest' | 'backpack';

// bag : la grille du sac à dos porté, ou les poches sans sac
export interface Loadout { primary: ItemInst | null; secondary: ItemInst | null; helmet: ItemInst | null; vest: ItemInst | null; backpack: ItemInst | null; bag: Grid; pouch: Grid }

// Amis, groupe et recherche de raid. pid : identifiant public d'un joueur (jamais son jeton ni sa session)
export type Presence = 'offline' | 'hideout' | 'queue' | 'raid';
export interface FriendView { pid: string; name: string; avatar: string | null; level: number; status: Presence }
export interface SocialView {
  me: string;
  friends: FriendView[]; incoming: FriendView[]; outgoing: FriendView[];
  group: { leader: string; members: FriendView[]; invited: FriendView[] } | null; // groupe de 2 ou 3 (null : seul)
  invites: FriendView[]; // chefs de groupe qui m'invitent
  queue: { since: number; searching: number } | null; // recherche en cours (heure du serveur, joueurs qui cherchent)
  cond: { now: RaidCond; changeAt: number; next: RaidCond }; // heure et météo du serveur
}

export interface ProfileView {
  name: string;
  credits: number;
  stash: Grid;
  loadout: Loadout;
  stats: { raids: number; extracts: number; deaths: number; kills: number };
  quests: QuestState;
  xp: number;
  hideout: HideoutState;
  avatar: string | null; // avatar Discord, si le compte est relié
}

// ---- Client → serveur
export type ClientMsg =
  | { t: 'hello'; name: string; token?: string }
  // Planque
  | { t: 'move'; from: Place; uid: number; to: Place; x?: number; y?: number } // planque, et rangement du sac en raid
  | { t: 'sell'; from: Place; uid: number }
  | { t: 'buy'; id: string }
  | { t: 'queue'; act: 'start' | 'cancel' } // recherche d'un raid (seul, ou le chef pour tout son groupe)
  | { t: 'friend'; act: 'search'; q: string }
  | { t: 'friend'; act: 'request' | 'accept' | 'decline' | 'remove'; pid: string }
  | { t: 'group'; act: 'invite' | 'accept' | 'decline' | 'leave' | 'kick'; pid: string } // accept / decline : pid du chef qui invite
  | { t: 'quest'; id: string; act: 'accept' | 'turnin' }
  | { t: 'upgrade'; module: string }
  | { t: 'craft'; recipe: string }
  | { t: 'collect'; module: string }
  | { t: 'sort' } // ranger le coffre
  | { t: 'repair'; from: Place; uid: number } // remettre une protection à neuf
  | { t: 'mod'; from: Place; uid: number; slot: ModSlot; mod: string | null } // monter (ou retirer) un accessoire d'arme
  | { t: 'give'; id: string; n: number } // test : se donner un objet (serveur local uniquement)
  // Raid
  | { t: 'in'; inputs: MoveInput[] }
  | { t: 'fire'; angle: number; view: number; pitch?: number } // pitch : inclinaison de la visée (radians, + vers le haut)
  | { t: 'reload' }
  | { t: 'slot'; s: 1 | 2 }
  | { t: 'open'; id: number }
  | { t: 'close' }
  | { t: 'unlock'; lock: string } // ouvrir une porte fermée à clé (la clé est dans le sac ou la poche)
  | { t: 'take'; cont: number; uid: number; to?: Place; x?: number; y?: number }
  | { t: 'put'; cont: number; uid: number; x?: number; y?: number }
  | { t: 'use'; uid: number }
  | { t: 'equip'; uid: number }
  | { t: 'ping'; at: number };

// Drapeaux d'état d'un joueur dans un instantané
export const F = { MOVING: 1, SPRINT: 2, CROUCH: 4, AIM: 8, DEAD: 16, RELOAD: 32, BOT: 64, HEAL: 128, SEARCH: 256, HELMET: 512, VEST: 1024, PACK: 2048, HMIL: 4096, VKIND: 8192, PKIND: 32768 } as const; // VKIND : 2 bits (8192, 16384) pour le type de gilet ; PKIND : 2 bits pour le sac
// Types visibles de casque et de gilet, codés dans les drapeaux d'un joueur
export const VEST_KINDS = ['v_soft', 'v_police', 'v_mil'];
export const gearFlags = (helmet: string | null | undefined, vest: string | null | undefined) =>
  (helmet ? F.HELMET : 0) | (helmet === 'h_mil' ? F.HMIL : 0) | (vest ? F.VEST + Math.max(0, VEST_KINDS.indexOf(vest)) * F.VKIND : 0);
export const helmetOf = (f: number) => (f & F.HELMET ? (f & F.HMIL ? 'h_mil' : 'h_hard') : null);
export const vestOf = (f: number) => (f & F.VEST ? VEST_KINDS[Math.floor(f / F.VKIND) & 3] ?? 'v_soft' : null);
export const PACK_KINDS = ['b_small', 'b_patrol', 'b_assault', 'b_raid'];
export const packFlags = (pack: string | null | undefined) => (pack ? F.PACK + Math.max(0, PACK_KINDS.indexOf(pack)) * F.PKIND : 0);
export const packOf = (f: number) => (f & F.PACK ? PACK_KINDS[Math.floor(f / F.PKIND) & 3] ?? 'b_patrol' : null);

// [id, x, y, angle, arme (indice WEAPON_ORDER, -1 sans arme), drapeaux, pv]
export type PlayerTuple = [number, number, number, number, number, number, number, number?, number?]; // … pv, hauteur des pieds (saut, sur un objet), visée en hauteur
// Infecté : [id, x, y, angle, type (0 rôdeur, 1 coureur), état (0 immobile, 1 marche, 2 course, 3 attaque, 4 mort)]
export type InfectedTuple = [number, number, number, number, number, number];
// Corps : [id, x, y, angle, apparence, conteneur]
export type BodyTuple = [number, number, number, number, number, number];

export type GameEvent =
  | { k: 'door'; lock: string } // porte fermée à clé ouverte (pour tout le raid)
  | { k: 'shot'; id: number; w: WeaponId; x: number; y: number; ends: [number, number, number, number][]; sup?: 1 } // [x, y, touché : 0 décor, 1 corps, 2 couvert bas, hauteur]
  | { k: 'step'; x: number; y: number; run: 0 | 1 }
  | { k: 'reload'; id: number; w: WeaponId; x: number; y: number }
  | { k: 'hitmark'; kill: 0 | 1; dmg: number; armor: 0 | 1 } // armor : la balle a été arrêtée par une protection
  | { k: 'hurt'; dmg: number; fromX: number; fromY: number; zone: 'head' | 'thorax' | 'limbs'; armor: 0 | 1 }
  | { k: 'kill'; killer: number; victim: number; w: WeaponId }
  | { k: 'groan'; x: number; y: number; loud: 0 | 1 }
  | { k: 'swipe'; x: number; y: number }
  | { k: 'search'; x: number; y: number }
  | { k: 'extracted'; id: number };

export interface Snapshot {
  t: 's';
  tick: number;
  ack: number; // dernière entrée de mouvement appliquée
  you: {
    // z, vz : hauteur des pieds et vitesse verticale (saut)
    x: number; y: number; z?: number; vz?: number; jcd?: number; hp: number; w: WeaponId | null; mag: number; reload: number; shot: number;
    heal: number; // progression d'un soin (0 à 1), 0 si aucun
    ext: number; extName: string; // progression d'extraction
    left: number; // secondes avant la fin du raid
    tally: [number, number, number]; // éliminations de ce raid : infectés, pillards, boss
    status: number; // 1 saignement, 2 fracture
    armor: [number, number, number, number]; // casque : durabilité, maximum ; gilet : durabilité, maximum
  };
  p: PlayerTuple[];
  z: InfectedTuple[];
  b: BodyTuple[];
  ev: GameEvent[];
  drops?: [number, number, number][]; // restes d'infectés à fouiller : meuble, x, y
  dead?: [number, number, number][]; // corps récents des pillards (repères) : x, y, secondes restantes
}

// Les objets pas encore découverts ne sont envoyés que par leur place dans la grille (pas leur nature)
export interface ContView { id: number; name: string; items: Grid; searching: boolean; hidden: number; slots: [number, number, number, number][]; next: number }

export type RaidResult = 'extracted' | 'dead' | 'mia';

export type ServerMsg =
  | { t: 'social'; social: SocialView }
  | { t: 'friendSearch'; results: FriendView[] }
  | { t: 'profile'; token: string; profile: ProfileView; prices: Record<string, number>; unlock: Record<string, number>; now: number; dev?: boolean } // unlock : niveau requis pour chaque offre ; now : heure du serveur (ms)
  | { t: 'joined'; id: number; room: string; tick: number; ends: number; open?: string[]; cond?: RaidCond } // open : portes déjà ouvertes dans ce raid
  | { t: 'inv'; loadout: Loadout; slot: 1 | 2 }
  | { t: 'cont'; cont: ContView | null }
  // kills : joueurs et pillards éliminés ; infected : infectés tués ; totalXp : expérience totale après le raid
  | { t: 'end'; result: RaidResult; value: number; killer: string; duration: number; kills: number; infected: number; fight?: { shots: number; hits: number; dealt: number; taken: number }; xp: number; totalXp: number; level: number; levelUp: boolean }
  | { t: 'roster'; players: { id: number; name: string; bot: boolean }[] }
  | { t: 'pong'; at: number; tick: number }
  | { t: 'toast'; msg: string }
  | { t: 'error'; msg: string }
  | Snapshot;
